import {
  DeiAgeRange,
  DeiDisabilityIdentify,
  DeiEthnicity,
  DeiFreeSchoolMeals,
  DeiGender,
  DeiNeurodivergenceIdentify,
  DeiReligiousBelief,
  DeiSexualOrientation,
  DeiStageActionFieldsModel,
  DeiStageActionResponsesFragment,
  DeiYesNo,
  FieldType,
} from 'generated/graphql';
import { omit, startCase } from 'lodash';
import {
  DeiErrors,
  DeiFields,
  deiPayloadKeys,
} from './StageActionDeiForm.interfaces';

// The order of the fields in the form is set by this object
export const deiFieldQuestions: Record<
  keyof DeiFields,
  { key: deiPayloadKeys | ''; description?: string }
> = {
  hasGenderIdentityField: {
    key: 'genderIdentity',
  },
  hasTransgenderIdentityField: {
    key: 'hasTransgenderIdentity',
  },
  hasSexualOrientationField: {
    key: 'sexualOrientation',
  },
  hasEthnicityField: {
    key: 'ethnicity',
  },
  hasReligiousBeliefsField: {
    key: 'religiousBelief',
  },
  hasDisabilityField: {
    description: 'hasDisabilityDescription',
    key: 'hasDisability',
  },
  hasNeurodiversityField: {
    description: 'hasNeurodiversityDescription',
    key: 'hasNeurodiversity',
  },

  hasNeurodivergenceIdentifyField: {
    key: 'neurodivergenceIdentify',
  },
  hasDisabilityIdentifyField: {
    key: 'disabilityIdentify',
  },
  hasFreeSchoolMealsField: {
    key: 'freeSchoolMeals',
  },
  hasAgeRangeField: {
    key: 'ageRange',
  },
  __typename: { key: '' },
};

const genderLabels = {
  [DeiGender.Male]: 'male',
  [DeiGender.Female]: 'female',
  [DeiGender.Intersex]: 'intersex',
  [DeiGender.NonBinary]: 'nonBinary',
  [DeiGender.GenderQueer]: 'genderQueer',
  [DeiGender.Other]: 'other',
  [DeiGender.PreferNotToSay]: 'preferNotToSay',
};

const sexualityLabels = {
  [DeiSexualOrientation.Asexual]: 'asexual',
  [DeiSexualOrientation.Bisexual]: 'bisexual',
  [DeiSexualOrientation.Gay]: 'gay',
  [DeiSexualOrientation.Straight]: 'heterosexual',
  [DeiSexualOrientation.Lesbian]: 'lesbian',
  [DeiSexualOrientation.Pansexual]: 'pansexual',
  [DeiSexualOrientation.Queer]: 'queer',
  [DeiSexualOrientation.Other]: 'other',
  [DeiSexualOrientation.PreferNotToSay]: 'preferNotToSay',
};

const ethnicityLabels = {
  [DeiEthnicity.Asian]: 'asian',
  [DeiEthnicity.Black]: 'black',
  [DeiEthnicity.Hispanic]: 'hispanic',
  [DeiEthnicity.IndigenousPeoples]: 'indigenousPeoples',
  [DeiEthnicity.MiddleEasternNorthAfrican]: 'middleEasternNorthAfrican',
  [DeiEthnicity.Mixed]: 'mixed',
  [DeiEthnicity.PacificIslander]: 'pacificIslander',
  [DeiEthnicity.White]: 'white',
  [DeiEthnicity.Other]: 'other',
  [DeiEthnicity.PreferNotToSay]: 'preferNotToSay',
};

const religiousBeliefLabels = {
  [DeiReligiousBelief.Agnosticism]: 'agnosticism',
  [DeiReligiousBelief.Buddhism]: 'buddhism',
  [DeiReligiousBelief.Christianity]: 'christianity',
  [DeiReligiousBelief.Hinduism]: 'hinduism',
  [DeiReligiousBelief.Islam]: 'islam',
  [DeiReligiousBelief.Judaism]: 'judaism',
  [DeiReligiousBelief.Secular]: 'secular',
  [DeiReligiousBelief.Sikhism]: 'sikhism',
  [DeiReligiousBelief.Spiritual]: 'spiritual',
  [DeiReligiousBelief.Other]: 'other',
  [DeiReligiousBelief.PreferNotToSay]: 'preferNotToSay',
};

const ageLabels = {
  [DeiAgeRange.NLessthan_18]: 'lessThan18',
  [DeiAgeRange.N18_30]: '18-30',
  [DeiAgeRange.N31_40]: '31-40',
  [DeiAgeRange.N41_50]: '41-50',
  [DeiAgeRange.N51_65]: '51-65',
  [DeiAgeRange.N65Plus]: '65+',
  [DeiAgeRange.PreferNotToSay]: 'preferNotToSay',
};

const yesNoLabels = {
  [DeiYesNo.Yes]: 'yes',
  [DeiYesNo.No]: 'no',
  [DeiYesNo.PreferNotToSay]: 'preferNotToSay',
};

const freeSchoolMealsLabels = {
  [DeiFreeSchoolMeals.Yes]: 'yes',
  [DeiFreeSchoolMeals.No]: 'no',
  [DeiFreeSchoolMeals.NotApplicable]: 'notApplicable',
  [DeiFreeSchoolMeals.PreferNotToSay]: 'preferNotToSay',
};

const neurodivergenceLabels = {
  [DeiNeurodivergenceIdentify.Autism]: 'autism',
  [DeiNeurodivergenceIdentify.Adhd]: 'adhd',
  [DeiNeurodivergenceIdentify.Dyslexia]: 'dyslexia',
  [DeiNeurodivergenceIdentify.Dyscalculia]: 'dyscalculia',
  [DeiNeurodivergenceIdentify.Dyspraxia]: 'dyspraxia',
  [DeiNeurodivergenceIdentify.Tourette]: 'tourette',
  [DeiNeurodivergenceIdentify.Other]: 'otherNeurodivergent',
  [DeiNeurodivergenceIdentify.None]: 'none',
  [DeiNeurodivergenceIdentify.PreferNotToSay]: 'preferNotToSay',
};

const disabilityIdentifyLabels = {
  [DeiDisabilityIdentify.PhysicalImpairment]: 'physicalImpairment',
  [DeiDisabilityIdentify.SensoryImpairment]: 'sensoryImpairment',
  [DeiDisabilityIdentify.MentalHealthCondition]: 'mentalHealthCondition',
  [DeiDisabilityIdentify.IntellectualDisability]: 'intellectualDisability',
  [DeiDisabilityIdentify.SpeechImpairment]: 'speechImpairment',
  [DeiDisabilityIdentify.ChronicIllness]: 'chronicIllness',
  [DeiDisabilityIdentify.NeurodivergentCondition]: 'neurodivergentCondition',
  [DeiDisabilityIdentify.Other]: 'otherDisability',
  [DeiDisabilityIdentify.None]: 'none',
  [DeiDisabilityIdentify.PreferNotToSay]: 'preferNotToSay',
};

const createSelectOptions = <T extends Record<string, string>>(
  labelsObject: T
): { label: string; value: keyof T }[] =>
  (Object.keys(labelsObject) as Array<keyof T>).map(key => ({
    label: labelsObject[key],
    value: key,
  }));

const yesNoOptions = createSelectOptions(yesNoLabels);

const mapFieldToOptions = {
  hasGenderIdentityField: createSelectOptions(genderLabels),
  hasTransgenderIdentityField: yesNoOptions,
  hasSexualOrientationField: createSelectOptions(sexualityLabels),
  hasEthnicityField: createSelectOptions(ethnicityLabels),
  hasReligiousBeliefsField: createSelectOptions(religiousBeliefLabels),
  hasDisabilityField: yesNoOptions,
  hasNeurodiversityField: yesNoOptions,
  hasAgeRangeField: createSelectOptions(ageLabels),
  hasNeurodivergenceIdentifyField: createSelectOptions(neurodivergenceLabels),
  hasDisabilityIdentifyField: createSelectOptions(disabilityIdentifyLabels),
  hasFreeSchoolMealsField: createSelectOptions(freeSchoolMealsLabels),
};

// Fields object contains not only the selected questions but all possible ones, this function
// will filter out the unused questions and then sort the fields in the desired order
const filterAndSortUsedFields = (fields: DeiStageActionFieldsModel) => {
  const orderedKeys = Object.keys(deiFieldQuestions);
  return Object.entries(fields)
    .filter(
      elem =>
        fields[elem[0] as keyof DeiStageActionFieldsModel] &&
        elem[0] !== '__typename'
    )
    .sort((a, b) => orderedKeys.indexOf(a[0]) - orderedKeys.indexOf(b[0]));
};

export const getDeiFieldsWithOptions = (fields: DeiStageActionFieldsModel) => {
  const sortedFieldsWithOptions = filterAndSortUsedFields(fields).flatMap(
    field => {
      const { key, description } =
        deiFieldQuestions[field[0] as keyof DeiFields];

      // Add question label, description and select options to the DEI field
      const newField = {
        id: key,
        description: description,
        options: mapFieldToOptions[field[0] as keyof typeof mapFieldToOptions],
        type: FieldType.SingleSelectField,
      };

      // If genderIdentity is a question in the form, the hasTransgenderIdentity question is also added along with it
      if (field[0] === 'hasGenderIdentityField') {
        return [
          newField,
          {
            id: 'hasTransgenderIdentity',
            options: mapFieldToOptions.hasTransgenderIdentityField,
            type: FieldType.SingleSelectField,
          },
        ];
      }

      return newField;
    }
  );

  return sortedFieldsWithOptions;
};

export const checkboxFields = ['neurodivergenceIdentify', 'disabilityIdentify'];

export const checkboxFieldsWithOther = [
  'neurodivergenceIdentifyOther',
  'disabilityIdentifyOther',
];

const deiKeysToFields: Record<deiPayloadKeys, keyof typeof mapFieldToOptions> =
  {
    genderIdentity: 'hasGenderIdentityField',
    hasTransgenderIdentity: 'hasTransgenderIdentityField',
    sexualOrientation: 'hasSexualOrientationField',
    ethnicity: 'hasEthnicityField',
    religiousBelief: 'hasReligiousBeliefsField',
    hasDisability: 'hasDisabilityField',
    hasNeurodiversity: 'hasNeurodiversityField',
    ageRange: 'hasAgeRangeField',
    neurodivergenceIdentify: 'hasNeurodivergenceIdentifyField',
    disabilityIdentify: 'hasDisabilityIdentifyField',
    freeSchoolMeals: 'hasFreeSchoolMealsField',
    neurodivergenceIdentifyOther: 'hasNeurodivergenceIdentifyField',
    disabilityIdentifyOther: 'hasDisabilityIdentifyField',
  };

/**
 * Initialize select field values
 * @param pastResponses - The past responses from the user
 * @returns The initialized select field values that contain either the select option that was previously selected or the default empty value. Note that the label is actually the translation key for i18n NOT the actual label text
 * @example input: past responses for all fields
 * {
 *   neurodivergenceIdentify: ['AUTISM', 'OTHER'],
 *   disabilityIdentify: ['PHYSICAL_IMPAIRMENT', 'OTHER'],
 *   neurodivergenceIdentifyOther: 'Something that the user typed in the text area. It can be anything. Max characters: 160',
 *   disabilityIdentifyOther: 'Something that the user typed in the text area. It can be anything. Max characters: 160',
 *   ageRange: 'N18_30',
 *   hasGenderIdentityField: 'MALE',
 *   hasTransgenderIdentity: 'NO',
 *   hasSexualOrientationField: 'HETEROSEXUAL',
 *   hasEthnicityField: 'WHITE',
 * }
 * @example output: {
 *   ageRange: { label: '18-30', value: 'N18_30' },
 *   hasGenderIdentityField: { label: 'male', value: 'MALE' },
 *   hasTransgenderIdentity: { label: 'no', value: 'NO' },
 *   hasSexualOrientationField: { label: 'heterosexual', value: 'HETEROSEXUAL' },
 *   hasEthnicityField: { label: 'white', value: 'WHITE' },
 * }
 */
export const initializeDeiValues = (
  pastResponses: DeiStageActionResponsesFragment
) => {
  // Get responses that are not null from the select fields
  const filteredResponses = Object.entries(pastResponses).filter(
    elem =>
      elem[0] !== '__typename' &&
      !checkboxFields?.includes(elem[0] as deiPayloadKeys) &&
      !checkboxFieldsWithOther?.includes(elem[0] as deiPayloadKeys) &&
      elem[1]
  );

  const prefilledValues = filteredResponses.reduce((acc, key) => {
    return Object.assign(acc, {
      [key[0]]: getSelectedOptionByField(key[0], key[1] as string),
    });
  }, {});

  return prefilledValues;
};

/**
 * Initialize checkbox values
 * @param pastResponses - The past responses from the user
 * @returns The initialized checkbox values that contain either the previous responses or empty values
 * @example input: past responses for all fields
 * {
 *   neurodivergenceIdentify: ['AUTISM', 'OTHER'],
 *   disabilityIdentify: ['PHYSICAL_IMPAIRMENT', 'OTHER'],
 *   neurodivergenceIdentifyOther: 'Something that the user typed in the text area. It can be anything. Max characters: 160',
 *   disabilityIdentifyOther: 'Something that the user typed in the text area. It can be anything. Max characters: 160',
 *   ageRange: 'N18_30',
 *   hasGenderIdentityField: 'MALE',
 *   hasTransgenderIdentity: 'NO',
 *   hasSexualOrientationField: 'HETEROSEXUAL',
 *   hasEthnicityField: 'WHITE',
 * }
 * @example input: empty responses from the user
 * @example output:
 * {
 *   neurodivergenceIdentify: ['AUTISM', 'OTHER'],
 *   disabilityIdentify: ['PHYSICAL_IMPAIRMENT', 'OTHER'],
 *   neurodivergenceIdentifyOther: 'Something that the user typed in the text area. It can be anything. Max characters: 160',
 *   disabilityIdentifyOther: 'Something that the user typed in the text area. It can be anything. Max characters: 160',
 * }
 */
export const initializeCheckboxValues = (
  pastResponses: DeiStageActionResponsesFragment
) => {
  return (
    Object.entries(pastResponses)
      // Only keep keys that belong to known checkbox fields
      .filter(
        ([key]) =>
          checkboxFields?.includes(key) ||
          checkboxFieldsWithOther?.includes(key)
      )
      .reduce((acc, [fieldName, fieldValue]) => {
        let value;

        if (fieldValue) {
          // Case 1: If there’s already a response saved, keep it as-is
          value = fieldValue;
        } else if (checkboxFieldsWithOther?.includes(fieldName)) {
          // Case 2: If it’s a checkbox with an "Other" text field but no saved value,
          // initialize with an empty string (user can type later)
          value = '';
        } else {
          // Case 3: For regular checkboxes with no saved value,
          // initialize with an empty array (no boxes selected yet)
          value = [];
        }

        return Object.assign(acc, { [fieldName]: value });
      }, {})
  );
};

export const initializeShowOther = (checkboxValues: {
  [key: string]: string[];
}) => {
  return Object.entries(checkboxValues).reduce((acc, key) => {
    return Object.assign(acc, {
      [key[0]]: key[1] ? key[1].includes('OTHER') : false,
    });
  }, {});
};

const getSelectedOptionByField = (
  fieldKey: string,
  fieldValue: string | null
) => {
  const field = deiKeysToFields[fieldKey as deiPayloadKeys];
  return (
    mapFieldToOptions[field].find(item => item.value === fieldValue) || {
      label: '',
      value: '',
    }
  );
};

export const OTHER_FIELD_PREFIX = 'other';

export const removeErrors = (errors: DeiErrors, id: deiPayloadKeys) => {
  const updatedErrors = omit(errors, id as deiPayloadKeys);
  return updatedErrors;
};

export const otherFieldId = (id: string) => {
  return `${id}${startCase(OTHER_FIELD_PREFIX)}` as deiPayloadKeys;
};
